import React, { useEffect, useMemo, useRef, useState } from "react";
import { Editdoc, Deleteicon } from "../../Icon";
import { FaPrint } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../utils/CONSTANTS";
import DailyProductionReportPDF from "./dailyProductionReportPDF";
import Header from "../../components/header";

const LS_KEY = (date) => `daily_reports_${date}`;

const getTodayISO = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
};

const readLocalForDate = (date) => {
  try {
    const raw = localStorage.getItem(LS_KEY(date));
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.error("local read error", e);
    return [];
  }
};

const writeLocalForDate = (date, arr) => {
  try {
    localStorage.setItem(LS_KEY(date), JSON.stringify(arr || []));
  } catch (e) {
    console.error("local write error", e);
  }
};

const saveLocalEntry = (entry) => {
  const date = entry.date || getTodayISO();
  const arr = readLocalForDate(date);
  const item = { ...entry };
  if (!item._id && !item._localId) item._localId = Date.now().toString();
  arr.push(item);
  writeLocalForDate(date, arr);
  return item;
};

const updateLocalEntry = (entry) => {
  const date = entry.date || getTodayISO();
  const arr = readLocalForDate(date);
  const idx = arr.findIndex((r) => (r._localId && entry._localId && r._localId === entry._localId));
  if (idx >= 0) {
    arr[idx] = entry;
    writeLocalForDate(date, arr);
    return true;
  }
  const idx2 = arr.findIndex((r) => r._id && entry._id && r._id === entry._id);
  if (idx2 >= 0) {
    arr[idx2] = entry;
    writeLocalForDate(date, arr);
    return true;
  }
  return false;
};

const deleteLocalEntry = (entry) => {
  const date = entry.date || getTodayISO();
  const arr = readLocalForDate(date);
  const filtered = arr.filter((r) => {
    if (entry._localId && r._localId) return r._localId !== entry._localId;
    if (entry._id && r._id) return r._id !== entry._id;
    return true;
  });
  writeLocalForDate(date, filtered);
};

const mergeBackendAndLocal = (backendArr = [], localArr = []) => {
  const out = [...backendArr];
  const backendIds = new Set(backendArr.map((b) => String(b._id)));
  localArr.forEach((l) => {
    if (l._id && backendIds.has(String(l._id))) return;
    out.push(l);
  });
  return out;
};

const deriveDateFromItem = (item) => {
  try {
    if (item.date) {
      const d = new Date(item.date);
      if (!isNaN(d)) return d.toISOString().slice(0, 10);
    }
    if (item.createdAt) {
      const d = new Date(item.createdAt);
      if (!isNaN(d)) return d.toISOString().slice(0, 10);
    }
    if (item.updatedAt) {
      const d = new Date(item.updatedAt);
      if (!isNaN(d)) return d.toISOString().slice(0, 10);
    }
    if (item._id && typeof item._id === "string" && item._id.length >= 8) {
      const ts = parseInt(item._id.substring(0, 8), 16) * 1000;
      const d = new Date(ts);
      if (!isNaN(d)) return d.toISOString().slice(0, 10);
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const formatDisplayDate = (item) => {
  const iso = deriveDateFromItem(item);
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

const readAllLocalReports = () => {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith("daily_reports_")) continue;
      const arr = JSON.parse(localStorage.getItem(key) || "[]");
      if (Array.isArray(arr)) out.push(...arr);
    }
  } catch (e) {
    console.error("local all-read error", e);
  }
  return out;
};

const DailyReportMenu = () => {
  // State
  const [reports, setReports] = useState([]);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [alloperatorName, setAlloperatorName] = useState([]);
  const [cardNoList, setCardNoList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const [selectedDate, setSelectedDate] = useState(getTodayISO());
  const [cardSearch, setCardSearch] = useState("");
  const [cardSearchApplied, setCardSearchApplied] = useState("");

  // state + refs for date-query support
  const [apiSupportsDateQuery, setApiSupportsDateQuery] = useState(true);
  const apiSupportsDateQueryRef = useRef(apiSupportsDateQuery);

  // guard to avoid concurrent fetches (helps with StrictMode double-invoke)
  const inFlightRef = useRef(false);

  const [formData, setFormData] = useState({
    machineNo: "",
    partNo: "",
    operatorName: "",
    customOperatorName: null,
    cycleTime: 0,
    count: 0,
    target: 0,
    actualCount: 0,
    effeciencyPercent: 0,
    startTime: "",
    stopTime: "",
    description: "",
    _id: null,
    date: getTodayISO(),
    _localId: null,
  });

  // keep ref and state in sync
  const setApiSupportsDateQueryBoth = (val) => {
    apiSupportsDateQueryRef.current = val;
    setApiSupportsDateQuery(val);
  };

  // Fetch operator names
  const fetchOperatorNames = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/product/getOperatorNames`);
      const allOperatorNames = (response.data.operators || [])
        .map((item) => item.customOperatorName)
        .filter((name) => name != null && name !== undefined);
      setAlloperatorName([...new Set(allOperatorNames)]);
    } catch (error) {
      console.error("Error fetching operator names:", error);
    }
  };

  const fetchCardNos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/order/getCardno`);
      if (response.status === 200) {
        const sortedCardNos = response.data.cardNos.sort((a, b) => Number(b.cardNo) - Number(a.cardNo));
        setCardNoList(sortedCardNos);
      }
    } catch (error) {
      console.error("Error fetching card numbers:", error.message);
    }
  };

  // robust getAllReports with concurrency guard + ref-based flag
  // If cardNoFilter is set, fetch across all dates for that card; otherwise filter by date.
  const getAllReports = async (date = selectedDate, cardNoFilter = cardSearch) => {
    // prevent concurrent runs
    if (inFlightRef.current) {
      console.debug("getAllReports aborted — already in flight");
      return;
    }
    inFlightRef.current = true;

    try {
      const cardQ = String(cardNoFilter || "").trim().toLowerCase();
      const byCard = !!cardQ;

      // normalize incoming date to yyyy-mm-dd
      let qDate = date;
      if (qDate instanceof Date) qDate = qDate.toISOString().slice(0, 10);
      else if (typeof qDate === "string") {
        const parts = qDate.split("-");
        if (parts.length === 3) qDate = `${parts[0].padStart(4, "0")}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
      }

      console.log("getAllReports, date =", qDate, "card =", cardQ || "(none)");

      let backend = [];

      if (!byCard && apiSupportsDateQueryRef.current) {
        const response = await axios.get(`${API_BASE_URL}/api/product/getdailyreport`, {
          params: { date: qDate },
          validateStatus: () => true,
        });

        if (response.status === 404) {
          console.info("Server returned 404 for getdailyreport?date= — disabling date-query attempts and falling back to no-param GET.");
          setApiSupportsDateQueryBoth(false);
        } else if (response.status === 200) {
          backend = response.data.isdata || [];
        } else {
          console.info("getdailyreport?date= returned status", response.status);
        }
      }

      // Card search, or date query unsupported / empty — use plain GET (all reports)
      if (byCard || backend.length === 0) {
        const resp2 = await axios.get(`${API_BASE_URL}/api/product/getdailyreport`, {
          validateStatus: () => true,
        });
        if (resp2.status === 200) backend = resp2.data.isdata || [];
        else if (!byCard) {
          const localOnly = readLocalForDate(qDate || getTodayISO());
          setReports(localOnly.map((item) => ({ ...item })));
          return;
        }
      }

      let filteredBackend;
      if (byCard) {
        filteredBackend = backend.filter((item) =>
          String(item.partNo ?? "").toLowerCase() === cardQ ||
          String(item.partNo ?? "").toLowerCase().includes(cardQ)
        );
      } else if (!qDate) {
        filteredBackend = backend.slice();
      } else {
        filteredBackend = backend.filter((item) => deriveDateFromItem(item) === qDate);
      }

      const localRaw = byCard ? readAllLocalReports() : readLocalForDate(qDate || getTodayISO());
      const local = byCard
        ? localRaw.filter(
            (item) =>
              String(item.partNo ?? "").toLowerCase() === cardQ ||
              String(item.partNo ?? "").toLowerCase().includes(cardQ)
          )
        : localRaw;

      let merged = mergeBackendAndLocal(filteredBackend, local);

      if (byCard) {
        // Prefer exact card match if any exist
        const exact = merged.filter(
          (item) => String(item.partNo ?? "").toLowerCase() === cardQ
        );
        if (exact.length > 0) merged = exact;

        merged = merged.sort((a, b) => {
          const da = deriveDateFromItem(a) || "";
          const db = deriveDateFromItem(b) || "";
          if (da !== db) return db.localeCompare(da);
          return String(b._id || b._localId || "").localeCompare(
            String(a._id || a._localId || "")
          );
        });
      }

      console.log(
        "getAllReports -> backend(all):",
        backend.length,
        "backend(filtered):",
        filteredBackend.length,
        "local:",
        local.length,
        "merged:",
        merged.length
      );

      setReports(merged.map((item) => ({ ...item })));
    } catch (error) {
      console.error("Network/other error fetching reports:", error?.message || error);
      const cardQ = String(cardNoFilter || "").trim().toLowerCase();
      let local = cardQ ? readAllLocalReports() : readLocalForDate(date || getTodayISO());
      if (cardQ) {
        local = local.filter((item) =>
          String(item.partNo ?? "").toLowerCase().includes(cardQ)
        );
      }
      setReports(local.map((item) => ({ ...item })));
      toast.error("Failed to load reports from server; showing local entries.");
    } finally {
      inFlightRef.current = false;
    }
  };

  // debug observer
  useEffect(() => {
    console.log("reports state updated -> length:", reports.length, "sample:", reports.slice(0, 5));
  }, [reports]);

  // initial setup: fetch operator names and cardNos only
  useEffect(() => {
    fetchOperatorNames();
    fetchCardNos();

    const handler = () => fetchOperatorNames();
    window.addEventListener("operatorListUpdated", handler);
    return () => window.removeEventListener("operatorListUpdated", handler);
  }, []);

  // debounce card search so typing does not refetch every keypress
  useEffect(() => {
    const t = setTimeout(() => setCardSearchApplied(cardSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [cardSearch]);

  // re-fetch when date or applied card filter changes
  useEffect(() => {
    setFormData((prev) => ({ ...prev, date: selectedDate }));
    getAllReports(selectedDate, cardSearchApplied);
  }, [selectedDate, cardSearchApplied]);

  // When a card is searched, table already contains matching rows (all dates)
  const filteredReports = useMemo(() => reports, [reports]);

  const isCardFilterActive = !!cardSearchApplied;

  const getMinutesDiff = (start, stop) => {
    if (!start || !stop) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = stop.split(":").map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;
    let diff = endMinutes - startMinutes;
    if (diff < 0) diff += 24 * 60;
    return diff;
  };

  useEffect(() => {
    const minutesDiff = getMinutesDiff(formData.startTime, formData.stopTime);
    let target = formData.cycleTime * minutesDiff + Number(formData.count);
    setFormData((prevData) => ({ ...prevData, target }));
  }, [formData.count, formData.cycleTime, formData.startTime, formData.stopTime]);

  useEffect(() => {
    let efficiency = ((formData.actualCount - formData.count) / (formData.target - formData.count)) * 100;
    efficiency = isNaN(efficiency) ? 0 : efficiency;
    setFormData((prevData) => ({ ...prevData, effeciencyPercent: efficiency.toFixed(2) }));
  }, [formData.actualCount, formData.target, formData.count]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      machineNo: "",
      partNo: "",
      operatorName: "",
      customOperatorName: null,
      cycleTime: 0,
      count: 0,
      target: 0,
      actualCount: 0,
      effeciencyPercent: 0,
      startTime: "",
      stopTime: "",
      description: "",
      _id: null,
      date: selectedDate,
      _localId: null,
    });
    setIsEditMode(false);
  };

  // Save (create) — will attempt server, fallback to local
  const handleSave = async () => {
    try {
      let url = `${API_BASE_URL}/api/product/postdailyreport`;
      let method = "post";
      let successMessage = "Report created successfully!";

      if (isEditMode && formData._id) {
        url = `${API_BASE_URL}/api/product/updatedailyreport`;
        successMessage = "Report updated successfully!";
      }

      // send formData (includes date)
      const response = await axios[method](url, formData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        toast.success(successMessage);

        // If server returned created doc with _id, store a local mirror (optional)
        const saved = response.data?.data || null;
        if (saved && saved._id) {
          const date = formData.date || selectedDate;
          const localArr = readLocalForDate(date);
          const filtered = localArr.filter((r) => !(r._localId && formData._localId && r._localId === formData._localId));
          filtered.push({ ...saved, date });
          writeLocalForDate(date, filtered);
        }

        getAllReports(selectedDate);
        fetchOperatorNames();
        try { window.dispatchEvent(new Event("operatorListUpdated")); } catch (e) { }
        resetForm();
      } else {
        saveLocalEntry(formData);
        toast.info("Saved locally (server returned unexpected response).");
        getAllReports(selectedDate);
        resetForm();
      }
    } catch (err) {
      console.warn("Save failed, saving locally:", err?.message || err);
      const localItem = saveLocalEntry(formData);
      setFormData((prev) => ({ ...prev, _localId: localItem._localId }));
      toast.success("Saved locally (offline fallback).");
      getAllReports(selectedDate);
      resetForm();
    }
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    try {
      if (selectedReport._id) {
        const id = selectedReport._id;
        const response = await axios.delete(`${API_BASE_URL}/api/product/deletedailyReport/${id}`);
        if (response.status === 200) {
          toast.success("Record deleted successfully!");
          deleteLocalEntry(selectedReport);
          getAllReports(selectedDate);
        } else {
          toast.error("Failed to delete on server.");
        }
      } else {
        deleteLocalEntry(selectedReport);
        toast.success("Local record deleted.");
        getAllReports(selectedDate);
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete record.");
    } finally {
      setShowDeleteModal(false);
      setSelectedReport(null);
    }
  };

  const handleEditClick = (report) => {
    const dateStr = deriveDateFromItem(report) || selectedDate;
    setSelectedDate(dateStr);
    setFormData({
      machineNo: report.machineNo || "",
      partNo: report.partNo || "",
      operatorName: report.operatorName || "",
      customOperatorName: report.customOperatorName || null,
      cycleTime: report.cycleTime || 0,
      count: report.count || 0,
      target: report.target || 0,
      actualCount: report.actualCount || 0,
      effeciencyPercent: report.effeciencyPercent || 0,
      startTime: report.startTime || "",
      stopTime: report.stopTime || "",
      description: report.description || "",
      _id: report._id || null,
      date: dateStr,
      _localId: report._localId || null,
    });
    setIsEditMode(Boolean(report));
  };

  const handleUpdate = async () => {
    try {
      if (formData._id) {
        const response = await axios.post(`${API_BASE_URL}/api/product/updatedailyreport`, formData, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.status === 201) {
          toast.success("Report updated");
          updateLocalEntry(formData);
          getAllReports(selectedDate);
          fetchOperatorNames();
          resetForm();
          return;
        }
      } else if (formData._localId) {
        updateLocalEntry(formData);
        toast.success("Local report updated");
        getAllReports(selectedDate);
        resetForm();
        return;
      } else {
        await handleSave();
      }
    } catch (err) {
      console.warn("Update failed, updating local copy", err);
      updateLocalEntry(formData);
      toast.success("Updated locally (fallback).");
      getAllReports(selectedDate);
      resetForm();
    }
  };

  return (
    <>
      <Header />
      <div className="p-2 bg-gray-100 min-h-screen flex flex-col items-center">

        {/* FORM SECTION */}
        <div id="report-form" className="bg-blue-200 shadow-xl w-full max-w-6xl mx-auto">
          <div className="flex items-center justify-between bg-gradient-to-r from-teal-600 to-teal-700 p-0.5">
            <span className="text-white font-semibold px-2 text-sm">
              {isEditMode ? "Edit Daily Production Report" : "Daily Production Report Form"}
            </span>
          </div>
          <div className="p-1">
            <div className="mb-1">
              <div className="grid grid-cols-4 bg-[#E0FFFF] gap-1 p-1">
                {/* Machine No */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Machine No.</label>
                  <select
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="machineNo"
                    value={formData.machineNo}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Machine</option>
                    {Array.from({ length: 19 }, (_, i) => (
                      <option key={i + 1} value={`CNC-${String(i + 1).padStart(2, "0")}`}>
                        CNC-{String(i + 1).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Card No */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Card No.</label>
                  <select
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="partNo"
                    value={formData.partNo}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Card No.</option>
                    {cardNoList.map((card, index) => (
                      <option key={index} value={card.cardNo}>
                        {card.cardNo}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date picker */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="date"
                    value={formData.date}
                    onChange={(e) => {
                      const normalized = e.target.value || "";
                      setFormData(prev => ({ ...prev, date: normalized }));
                      setSelectedDate(normalized);
                      getAllReports(normalized);
                    }}
                  />
                </div>

                {/* Operator */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Operator Name</label>
                  <select
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="operatorName"
                    value={formData.operatorName}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Operator</option>
                    {alloperatorName.map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {formData.operatorName === "Other" && (
                    <input
                      type="text"
                      name="customOperatorName"
                      placeholder="Enter operator name"
                      value={formData.customOperatorName || ""}
                      onChange={(e) => setFormData({ ...formData, customOperatorName: e.target.value })}
                      className="w-full border border-gray-300 rounded p-0.5 text-xs mt-0.5"
                    />
                  )}
                </div>

                {/* Cycle Time */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Cycle Time</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="cycleTime"
                    value={formData.cycleTime}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Count */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Count (8:30)</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="count"
                    value={formData.count}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Start Time */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Start Time</label>
                  <input
                    type="time"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Stop Time */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Stop Time</label>
                  <input
                    type="time"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="stopTime"
                    value={formData.stopTime}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Target */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Target</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm bg-gray-100"
                    name="target"
                    value={formData.target}
                    readOnly
                  />
                </div>

                {/* Actual Count */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Actual Count</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="actualCount"
                    value={formData.actualCount}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Efficiency % */}
                <div className="col-span-1">
                  <label className="block mb-0.5 text-xs">Efficiency %</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm bg-gray-100"
                    name="effeciencyPercent"
                    value={`${formData.effeciencyPercent}%`}
                    readOnly
                  />
                </div>

                <div className="col-span-1"></div>

                {/* Reason */}
                <div className="col-span-4">
                  <label className="block mb-0.5 text-xs">Reason</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            {/* Form Buttons */}
            <div className="flex justify-between mt-1 bg-teal-600 p-1">
              <div className="flex gap-1">
                <button
                  className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                {isEditMode ? (
                  <button
                    className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                    onClick={handleUpdate}
                  >
                    Update
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                    onClick={handleSave}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLE SECTION */}
        <div className="w-full max-w-6xl mx-auto bg-white shadow-md p-4 rounded-lg mt-4">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold">Daily Production Report</h1>
              <input
                type="text"
                placeholder="Search Card No."
                value={cardSearch}
                onChange={(e) => setCardSearch(e.target.value)}
                className="border border-gray-300 rounded px-2 py-0.5 text-xs w-32"
              />
              {isCardFilterActive && (
                <span className="text-xs text-teal-700">
                  Showing all dates for card {cardSearchApplied}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!isCardFilterActive && (
                <div className="flex items-center gap-2">
                  <label className="text-sm">Date:</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      const normalized = e.target.value || "";
                      setSelectedDate(normalized);
                      setFormData((prev) => ({ ...prev, date: normalized }));
                      console.log("date changed ->", normalized);
                    }}
                    className="border rounded px-1 py-0.5 text-sm"
                  />
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  className="bg-black text-white px-3 py-1.5 rounded-md hover:bg-gray-800 flex items-center text-sm disabled:opacity-50"
                  onClick={() => setShowPDFModal(true)}
                  disabled={isCardFilterActive}
                  title={
                    isCardFilterActive
                      ? "Clear card filter to print by date"
                      : "Print"
                  }
                >
                  <FaPrint className="mr-1" /> Print
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 p-1">Sr.no</th>
                  {isCardFilterActive && (
                    <th className="border border-gray-300 p-1">Date</th>
                  )}
                  <th className="border border-gray-300 p-1">Machine .no</th>
                  <th className="border border-gray-300 p-1">Card .no</th>
                  <th className="border border-gray-300 p-1">Operator Name</th>
                  <th className="border border-gray-300 p-1">Cycle Time</th>
                  <th className="border border-gray-300 p-1">Count (8:30AM)</th>
                  <th className="border border-gray-300 p-1">Target (6:00PM)</th>
                  <th className="border border-gray-300 p-1">Actual Count</th>
                  <th className="border border-gray-300 p-1">%</th>
                  <th className="border border-gray-300 p-1">Start Time</th>
                  <th className="border border-gray-300 p-1">Stop Time</th>
                  <th className="border border-gray-300 p-1">Reason</th>
                  <th className="border border-gray-300 p-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report, index) => (
                  <tr key={report._id ?? report._localId ?? `${report.machineNo}-${index}`} className="text-center">
                    <td className="border border-gray-300 p-1">{index + 1}</td>
                    {isCardFilterActive && (
                      <td className="border border-gray-300 p-1 whitespace-nowrap">
                        {formatDisplayDate(report)}
                      </td>
                    )}
                    <td className="border border-gray-300 p-1">{report.machineNo}</td>
                    <td className="border border-gray-300 p-1">{report.partNo}</td>
                    <td className="border border-gray-300 p-1">{report.customOperatorName ?? report.operatorName}</td>
                    <td className="border border-gray-300 p-1">{report.cycleTime}</td>
                    <td className="border border-gray-300 p-1">{report.count}</td>
                    <td className="border border-gray-300 p-1">{report.target}</td>
                    <td className="border border-gray-300 p-1">{report.actualCount}</td>
                    <td className="border border-gray-300 p-1">{report.effeciencyPercent}%</td>
                    <td className="border border-gray-300 p-1">{report.startTime}</td>
                    <td className="border border-gray-300 p-1">{report.stopTime}</td>
                    <td className="border border-gray-300 p-1">{report.description}</td>
                    <td className="border border-gray-300 p-1">
                      <div className="flex justify-around">
                        <button
                          className="flex flex-col items-center px-1 py-0.5 rounded-md hover:bg-slate-300 text-xs"
                          onClick={() => handleEditClick(report)}
                        >
                          <Editdoc />
                          <div>Edit</div>
                        </button>
                        <button
                          className="flex flex-col items-center px-1 py-0.5 rounded-md hover:bg-slate-300 text-xs"
                          onClick={() => {
                            setSelectedReport(report);
                            setShowDeleteModal(true);
                          }}
                        >
                          <Deleteicon />
                          <div>Delete</div>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredReports.length === 0 && (
                  <tr>
                    <td
                      colSpan={isCardFilterActive ? 14 : 13}
                      className="text-center p-4 text-sm text-gray-600"
                    >
                      {isCardFilterActive
                        ? `No reports found for card no. ${cardSearchApplied}.`
                        : "No records for this date."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        {showDeleteModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded-md shadow-md">
              <h2 className="text-lg font-semibold mb-2">Confirm Deletion</h2>
              <p className="text-sm">Are you sure you want to delete this report?</p>
              <div className="flex justify-end mt-3">
                <button
                  className="bg-gray-300 px-3 py-1 rounded-md mr-2 text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-500 text-white px-3 py-1 rounded-md text-sm"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showPDFModal && (
          <div className="fixed inset-0 pt-12 flex flex-col items-center justify-center bg-black bg-opacity-50">
            <div className="flex justify-end w-full max-w-7xl">
              <button
                onClick={() => setShowPDFModal(false)}
                className="mb-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Close
              </button>
            </div>
            <DailyProductionReportPDF data={reports} printDate={selectedDate} />
          </div>
        )}

      </div>
    </>
  );
};

export default DailyReportMenu;
